import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  requestDeleteAccountAction,
  confirmDeleteAccountAction,
  getUserProfileAction,
} from "../../../redux/actions";
import { showSnackbar } from "../../../redux/slices/SnackbarSlice";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../utils/routes";
import Modal from "../../../components/MUIBaseComponents/Modal";
import "./AgencyAccount.scss";

interface AgencyAccountProps {
  styles: any;
}

const AgencyAccount: React.FC<AgencyAccountProps> = ({ styles }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { dashboardData, loading: profileLoading } = useSelector(
    (state: RootState) => state.dashboard,
  );
  console.log("dashboardData", dashboardData);

  // Determine workspace ownership from profile API
  // API returns workspaceMembership as a single object (not an array)
  const workspaceMembership = (dashboardData as any)?.workspaceMembership;
  const isWorkspaceOwner: boolean =
    workspaceMembership != null && workspaceMembership.isOwner === true;

  useEffect(() => {
    dispatch(getUserProfileAction());
  }, []);

  // Modal & Flow States
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false);
  const [showOtpModal, setShowOtpModal] = useState<boolean>(false);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  // Response data preview
  const [deletePreview, setDeletePreview] = useState<any>(null);

  // 6-digit OTP Input state
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(""));
  const [otpError, setOtpError] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handler for initiating account deletion
  const handleRequestDeletion = async () => {
    setIsRequestLoading(true);
    try {
      const res: any = await dispatch(requestDeleteAccountAction()).unwrap();
      setIsRequestLoading(false);
      const preview = res?.data?.preview || res?.preview || null;
      setDeletePreview(preview);
      setShowRequestModal(false);
      setShowOtpModal(true);
      setOtpDigits(Array(6).fill(""));
      setOtpError("");
      dispatch(
        showSnackbar({
          message:
            res?.message || "A verification code has been sent to your email.",
          type: "success",
        }),
      );
    } catch (err: any) {
      setIsRequestLoading(false);
      dispatch(
        showSnackbar({
          message:
            err?.message ||
            "Could not request account deletion. Please try again.",
          type: "error",
        }),
      );
    }
  };

  // OTP Digits change handlers
  const handleOtpChange = (index: number, value: string) => {
    const char = value.slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = char;
    setOtpDigits(newDigits);
    setOtpError("");

    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const digits = pasted.split("");
    const newDigits = [...otpDigits];
    digits.forEach((d, i) => {
      newDigits[i] = d;
    });
    setOtpDigits(newDigits);
    setOtpError("");
    const focusIndex = Math.min(digits.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  // Handler for confirming account deletion with OTP
  const handleConfirmDeletion = async () => {
    const fullOtp = otpDigits.join("").trim();
    if (fullOtp.length < 6) {
      setOtpError("Please enter all 6 digits of the verification code");
      return;
    }
    setOtpError("");
    setIsConfirmLoading(true);

    try {
      const res: any = await dispatch(
        confirmDeleteAccountAction({ otp: fullOtp }),
      ).unwrap();
      setIsConfirmLoading(false);
      setShowOtpModal(false);

      const successMsg =
        res?.message || res?.data?.message || "Account successfully deleted.";
      dispatch(
        showSnackbar({
          message: successMsg,
          type: "success",
        }),
      );

      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        navigate(routes.login || "/login");
        window.location.reload();
      }, 1200);
    } catch (err: any) {
      setIsConfirmLoading(false);
      const errorMsg =
        err?.message ||
        err?.data?.message ||
        "Failed to confirm account deletion.";
      setOtpError(errorMsg);
      dispatch(
        showSnackbar({
          message: errorMsg,
          type: "error",
        }),
      );
    }
  };

  return (
    <Box sx={styles.sectionContainer}>
      {/* Header */}
      <Typography sx={styles.sectionTitle}>Account Settings</Typography>
      <Typography
        sx={{
          ...styles.typography,
          color: "#6b7280",
          fontSize: "14px",
          mb: 3,
        }}
      >
        Manage your account configuration and permanent account deletion.
      </Typography>

      {/* Delete Account Card */}
      <Box
        sx={{
          p: { xs: 3, sm: 3.5 },
          borderRadius: "16px",
          border: "1px solid #fee2e2",
          backgroundColor: "#fff5f5",
          maxWidth: "750px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-family-manrope)",
            fontSize: "18px",
            fontWeight: 600,
            color: "#e53e3e",
            mb: 1.5,
          }}
        >
          Delete Account
        </Typography>

        <Typography
          sx={{
            fontFamily: "var(--font-family-manrope)",
            fontSize: "14px",
            fontWeight: 400,
            color: "#4b5563",
            mb: 3,
            lineHeight: 1.5,
            maxWidth: "600px",
          }}
        >
          {isWorkspaceOwner
            ? "Permanently delete your account, workspace, businesses, posts, and subscriptions. This action cannot be undone."
            : "Account deletion is only available to the workspace owner. Please contact your workspace owner to delete the account."}
        </Typography>

        <Tooltip
          title={
            !isWorkspaceOwner
              ? "Only the workspace owner can delete the account"
              : ""
          }
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#ffffff",
                color: "#5e5d5d",
                border: "1px solid #d8d8d8",
                fontSize: "12px",
                borderRadius: "8px",
                fontWeight: 500,
                p: 1,
                fontFamily: "var(--font-family-manrope)",
              },
            },
            arrow: {
              sx: { color: "#d8d8d8" },
            },
          }}
        >
          <span>
            <Button
              variant="contained"
              onClick={() => setShowRequestModal(true)}
              disabled={!isWorkspaceOwner || profileLoading}
              disableElevation
              sx={{
                width: "auto",
                backgroundColor: "#f04438",
                color: "#fff",
                border: "none",
                boxShadow: "none",
                cursor: isWorkspaceOwner ? "pointer" : "not-allowed",
                textTransform: "none",
                padding: "var(--action-button-small-padding, 6px 20px)",
                borderRadius: "var(--action-button-radius, 8px)",
                fontFamily: "var(--font-family-manrope)",
                fontSize: "var(--buttons-font-size, 16px)",
                transition: "all 0.2s ease-in-out",
                fontWeight: "400 !important",
                "&:hover": {
                  backgroundColor: "#f04438",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                },
              }}
            >
              Delete Account
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* STEP 1: Request Account Deletion Modal */}
      <Modal
        width={500}
        verticalAlign="center"
        title="Delete your account?"
        className="modal-manage-alerts"
        containerClassName="modal-manage-alerts-wrap"
        open={showRequestModal}
        onClose={() => {
          if (!isRequestLoading) setShowRequestModal(false);
        }}
      >
        <div className="manage-alerts-modal-container">
          <Typography
            sx={{
              fontFamily: "var(--font-family-manrope)",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              lineHeight: 1.6,
              m: 0,
            }}
          >
            This is permanent and cannot be undone. There is no recovery window
            and no backup restore once the deletion is confirmed. Please make
            sure you understand what will be removed.
          </Typography>

          <Box
            sx={{
              backgroundColor: "#fff5f5",
              border: "1px solid #fee2e2",
              borderRadius: "14px",
              p: "16px 18px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-family-manrope)",
                fontSize: "14px",
                fontWeight: 500,
                color: "#e53e3e",
                lineHeight: 1.6,
              }}
            >
              This deletes your profile, businesses, posts, and subscriptions
              for good. If you own a workspace, everyone in it loses access too.
            </Typography>
          </Box>

          <div className="actions-footer">
            <Button
              onClick={() => setShowRequestModal(false)}
              disabled={isRequestLoading}
              sx={{
                backgroundColor: "#ffffff",
                border: "1px solid #E0E0E0",
                padding: "var(--buttons-padding, 8px 16px)",
                fontSize: "var(--buttons-font-size, 14px)",
                color: "var(--buttons-font-color, #1e293b)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textTransform: "none",
                fontWeight: "400 !important",
                fontFamily: "var(--font-family-manrope)",
                boxShadow: "none",
                borderRadius: "var(--action-button-radius, 8px)",
                "&:hover": {
                  boxShadow: "none",
                  color: "var(--buttons-font-color, #1e293b)",
                  backgroundColor: "#ffffff",
                  border: "1px solid #E0E0E0",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestDeletion}
              disabled={isRequestLoading}
              variant="contained"
              sx={{
                backgroundColor: "#f04438",
                color: "white",
                width: "auto",
                borderRadius: "var(--action-button-radius, 8px)",
                border: "none",
                padding: "var(--buttons-padding, 8px 16px)",
                fontSize: "var(--medium-body-font-size, 14px)",
                fontFamily: "var(--font-family-manrope)",
                fontWeight: "400 !important",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "#f04438",
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
              }}
            >
              {isRequestLoading ? "Deleting..." : "Delete Account"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* STEP 2: Confirm Account Deletion Modal (Preview & 6-Digit OTP) */}
      <Modal
        width={520}
        verticalAlign="center"
        title="Confirm account deletion"
        className="modal-manage-alerts"
        containerClassName="modal-manage-alerts-wrap"
        open={showOtpModal}
        onClose={() => {
          if (!isConfirmLoading) setShowOtpModal(false);
        }}
      >
        <div className="manage-alerts-modal-container">
          <Typography
            sx={{
              fontFamily: "var(--font-family-manrope)",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              lineHeight: 1.6,
              m: 0,
            }}
          >
            We sent a 6-digit verification code to your email. It expires in 10
            minutes. Enter it below to permanently delete your account.
          </Typography>

          {/* What will be deleted preview card */}
          {deletePreview && (
            <Box
              sx={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "16px",
                p: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-family-manrope)",
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                What will be deleted
              </Typography>

              {deletePreview.ownsWorkspace && deletePreview.workspace?.name && (
                <Typography
                  sx={{
                    fontFamily: "var(--font-family-manrope)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#f04438",
                  }}
                >
                  Workspace &quot;{deletePreview.workspace.name}&quot; and
                  everything in it
                </Typography>
              )}

              {typeof deletePreview.workspaceMembersRemoved === "number" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Workspace members removed
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {deletePreview.workspaceMembersRemoved}
                  </Typography>
                </Box>
              )}

              {typeof deletePreview.checkinBusinessesDeleted === "number" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Check-in businesses deleted
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {deletePreview.checkinBusinessesDeleted}
                  </Typography>
                </Box>
              )}

              {typeof deletePreview.rankTrackerBusinessesDeleted ===
                "number" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Rank tracker businesses deleted
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {deletePreview.rankTrackerBusinessesDeleted}
                  </Typography>
                </Box>
              )}

              {typeof deletePreview.stripeSubscriptionsToCancel ===
                "number" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Subscriptions to cancel
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "var(--font-family-manrope)",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {deletePreview.stripeSubscriptionsToCancel}
                  </Typography>
                </Box>
              )}

              {typeof deletePreview.postsDeleted === "number" &&
                deletePreview.postsDeleted > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      Posts deleted
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {deletePreview.postsDeleted}
                    </Typography>
                  </Box>
                )}

              {typeof deletePreview.otherWorkspaceMembershipsLeft ===
                "number" &&
                deletePreview.otherWorkspaceMembershipsLeft > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      Other workspace memberships left
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {deletePreview.otherWorkspaceMembershipsLeft}
                    </Typography>
                  </Box>
                )}
            </Box>
          )}

          {/* 6 Digit OTP Inputs */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              {otpDigits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputRefs.current[idx] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  onPaste={handleOtpPaste}
                  style={{
                    width: "100%",
                    height: "50px",
                    borderRadius: "14px",
                    border: otpError
                      ? "1.5px solid #ef4444"
                      : "1.5px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                    fontSize: "20px",
                    fontWeight: 700,
                    textAlign: "center",
                    fontFamily: "var(--font-family-manrope)",
                    color: "#111827",
                    outline: "none",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.backgroundColor = "#ffffff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = otpError
                      ? "#ef4444"
                      : "#e2e8f0";
                    e.target.style.backgroundColor = "#f8fafc";
                  }}
                />
              ))}
            </Box>
            {otpError && (
              <Typography
                sx={{
                  fontFamily: "var(--font-family-manrope)",
                  fontSize: "12px",
                  color: "#ef4444",
                  mt: 0.5,
                }}
              >
                {otpError}
              </Typography>
            )}
          </Box>

          <div className="actions-footer">
            <Button
              onClick={() => setShowOtpModal(false)}
              disabled={isConfirmLoading}
              sx={{
                backgroundColor: "#ffffff",
                border: "1px solid #E0E0E0",
                padding: "var(--buttons-padding, 8px 16px)",
                fontSize: "var(--buttons-font-size, 14px)",
                color: "var(--buttons-font-color, #1e293b)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textTransform: "none",
                fontWeight: "400 !important",
                fontFamily: "var(--font-family-manrope)",
                boxShadow: "none",
                borderRadius: "var(--action-button-radius, 8px)",
                "&:hover": {
                  boxShadow: "none",
                  color: "var(--buttons-font-color, #1e293b)",
                  backgroundColor: "#ffffff",
                  border: "1px solid #E0E0E0",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeletion}
              disabled={isConfirmLoading || otpDigits.join("").length < 6}
              variant="contained"
              sx={{
                backgroundColor: "#f04438",
                color: "white",
                width: "auto",
                borderRadius: "var(--action-button-radius, 8px)",
                border: "none",
                padding: "var(--buttons-padding, 8px 16px)",
                fontSize: "var(--medium-body-font-size, 14px)",
                fontFamily: "var(--font-family-manrope)",
                fontWeight: "400 !important",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "#f04438",
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
              }}
            >
              {isConfirmLoading ? "Deleting..." : "Permanently Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </Box>
  );
};

export default AgencyAccount;
